import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Star,
  Target,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Save,
  X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const REVIEW_TYPES = ['Annual', 'Semi-Annual', 'Quarterly', 'Probationary', '90-Day', 'Promotion'];
const GOAL_STATUSES = ['Not Started', 'In Progress', 'Completed', 'Cancelled'];

export default function PersonnelPerformance({ personnelId, personName, isAdmin = true }) {
  const [reviews, setReviews] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewReview, setShowNewReview] = useState(false);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState({});
  
  const [newReview, setNewReview] = useState({
    review_type: 'Annual',
    review_period_start: '',
    review_period_end: ''
  });
  
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    target_date: ''
  });

  useEffect(() => {
    fetchData();
  }, [personnelId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, goalsRes] = await Promise.all([
        axios.get(`${API}/personnel/${personnelId}/reviews`),
        axios.get(`${API}/personnel/${personnelId}/goals`)
      ]);
      setReviews(reviewsRes.data.reviews || []);
      setGoals(goalsRes.data.goals || []);
    } catch (error) {
      console.error('Failed to load performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async () => {
    if (!newReview.review_period_start || !newReview.review_period_end) {
      toast.error('Please select review period dates');
      return;
    }
    try {
      await axios.post(`${API}/personnel/${personnelId}/reviews`, newReview);
      toast.success('Performance review created');
      setShowNewReview(false);
      setNewReview({ review_type: 'Annual', review_period_start: '', review_period_end: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create review');
    }
  };

  const handleUpdateReview = async (reviewId, data) => {
    try {
      await axios.put(`${API}/personnel/${personnelId}/reviews/${reviewId}`, data);
      toast.success('Review updated');
      setEditingReview(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/personnel/${personnelId}/reviews/${reviewId}`);
      toast.success('Review deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.title) {
      toast.error('Please enter a goal title');
      return;
    }
    try {
      await axios.post(`${API}/personnel/${personnelId}/goals`, newGoal);
      toast.success('Goal created');
      setShowNewGoal(false);
      setNewGoal({ title: '', description: '', target_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const handleUpdateGoal = async (goalId, data) => {
    try {
      await axios.put(`${API}/personnel/${personnelId}/goals/${goalId}`, data);
      toast.success('Goal updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/personnel/${personnelId}/goals/${goalId}`);
      toast.success('Goal deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Self-Assessment': 'bg-blue-100 text-blue-800',
      'Manager Review': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Not Started': 'bg-gray-100 text-gray-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getRatingStars = (rating) => {
    if (!rating) return <span className="text-muted-foreground text-sm">Not rated</span>;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            size={14}
            className={i <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
        <span className="text-sm ml-1">{rating}/5</span>
      </div>
    );
  };

  const toggleReviewExpanded = (reviewId) => {
    setExpandedReviews(prev => ({ ...prev, [reviewId]: !prev[reviewId] }));
  };

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading performance data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Performance Reviews Section */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Star size={18} />
            Performance Reviews
          </h3>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowNewReview(true)} data-testid="new-review-btn">
              <Plus size={14} className="mr-1" /> New Review
            </Button>
          )}
        </div>

        {/* New Review Form */}
        {showNewReview && (
          <Card className="p-4 mb-4 bg-muted/50">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium">Review Type</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newReview.review_type}
                  onChange={(e) => setNewReview({ ...newReview, review_type: e.target.value })}
                >
                  {REVIEW_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Period Start</label>
                <Input
                  type="date"
                  value={newReview.review_period_start}
                  onChange={(e) => setNewReview({ ...newReview, review_period_start: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Period End</label>
                <Input
                  type="date"
                  value={newReview.review_period_end}
                  onChange={(e) => setNewReview({ ...newReview, review_period_end: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreateReview}>Create Review</Button>
              <Button size="sm" variant="outline" onClick={() => setShowNewReview(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No performance reviews</p>
        ) : (
          <div className="space-y-3">
            {reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((review) => (
              <div key={review.id} className="border rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleReviewExpanded(review.id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{review.review_type} Review</span>
                    <span className="text-sm text-muted-foreground">
                      {review.review_period_start} → {review.review_period_end}
                    </span>
                    {getStatusBadge(review.status)}
                  </div>
                  <div className="flex items-center gap-2">
                    {getRatingStars(review.overall_rating)}
                    {expandedReviews[review.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {expandedReviews[review.id] && (
                  <div className="p-4 border-t bg-muted/30">
                    {editingReview === review.id ? (
                      <ReviewEditForm
                        review={review}
                        onSave={(data) => handleUpdateReview(review.id, data)}
                        onCancel={() => setEditingReview(null)}
                      />
                    ) : (
                      <div className="space-y-4">
                        {/* Ratings Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <div>
                            <label className="text-xs text-muted-foreground">Overall</label>
                            {getRatingStars(review.overall_rating)}
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Quality</label>
                            {getRatingStars(review.quality_rating)}
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Productivity</label>
                            {getRatingStars(review.productivity_rating)}
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Communication</label>
                            {getRatingStars(review.communication_rating)}
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Teamwork</label>
                            {getRatingStars(review.teamwork_rating)}
                          </div>
                        </div>

                        {/* Comments */}
                        {review.manager_assessment && (
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Manager Assessment</label>
                            <p className="text-sm mt-1">{review.manager_assessment}</p>
                          </div>
                        )}
                        {review.strengths && (
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Strengths</label>
                            <p className="text-sm mt-1">{review.strengths}</p>
                          </div>
                        )}
                        {review.areas_for_improvement && (
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Areas for Improvement</label>
                            <p className="text-sm mt-1">{review.areas_for_improvement}</p>
                          </div>
                        )}

                        {/* Actions */}
                        {isAdmin && (
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" variant="outline" onClick={() => setEditingReview(review.id)}>
                              <Edit2 size={14} className="mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteReview(review.id)}>
                              <Trash2 size={14} className="mr-1" /> Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Goals Section */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Target size={18} />
            Performance Goals
          </h3>
          <Button size="sm" onClick={() => setShowNewGoal(true)} data-testid="new-goal-btn">
            <Plus size={14} className="mr-1" /> New Goal
          </Button>
        </div>

        {/* New Goal Form */}
        {showNewGoal && (
          <Card className="p-4 mb-4 bg-muted/50">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium">Goal Title</label>
                <Input
                  placeholder="Enter goal title..."
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Description</label>
                <Input
                  placeholder="Goal description..."
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Target Date</label>
                <Input
                  type="date"
                  value={newGoal.target_date}
                  onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={handleCreateGoal}>Create Goal</Button>
              <Button size="sm" variant="outline" onClick={() => setShowNewGoal(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Goals List */}
        {goals.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No performance goals</p>
        ) : (
          <div className="space-y-3">
            {goals.map((goal) => (
              <div key={goal.id} className="p-3 border rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{goal.title}</span>
                      {getStatusBadge(goal.status)}
                    </div>
                    {goal.description && (
                      <p className="text-sm text-muted-foreground">{goal.description}</p>
                    )}
                    {goal.target_date && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Target: {goal.target_date}
                      </p>
                    )}
                    {/* Progress Bar */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span>Progress</span>
                        <span>{goal.progress_percent || 0}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${goal.progress_percent || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-4">
                    <select
                      className="text-xs border rounded px-2 py-1"
                      value={goal.status}
                      onChange={(e) => handleUpdateGoal(goal.id, { status: e.target.value })}
                    >
                      {GOAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 h-8 w-8 p-0"
                      onClick={() => handleDeleteGoal(goal.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// Review Edit Form Component
function ReviewEditForm({ review, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    status: review.status,
    overall_rating: review.overall_rating || '',
    quality_rating: review.quality_rating || '',
    productivity_rating: review.productivity_rating || '',
    communication_rating: review.communication_rating || '',
    teamwork_rating: review.teamwork_rating || '',
    manager_assessment: review.manager_assessment || '',
    strengths: review.strengths || '',
    areas_for_improvement: review.areas_for_improvement || '',
    goals_for_next_period: review.goals_for_next_period || ''
  });

  const RatingSelect = ({ label, value, field }) => (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <select
        className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
        value={value || ''}
        onChange={(e) => setFormData({ ...formData, [field]: e.target.value ? parseInt(e.target.value) : null })}
      >
        <option value="">Not rated</option>
        {[1, 2, 3, 4, 5].map(r => <option key={r} value={r}>{r} - {['Poor', 'Below Average', 'Average', 'Good', 'Excellent'][r-1]}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium">Status</label>
        <select
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
        >
          <option value="Draft">Draft</option>
          <option value="Self-Assessment">Self-Assessment</option>
          <option value="Manager Review">Manager Review</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <RatingSelect label="Overall" value={formData.overall_rating} field="overall_rating" />
        <RatingSelect label="Quality" value={formData.quality_rating} field="quality_rating" />
        <RatingSelect label="Productivity" value={formData.productivity_rating} field="productivity_rating" />
        <RatingSelect label="Communication" value={formData.communication_rating} field="communication_rating" />
        <RatingSelect label="Teamwork" value={formData.teamwork_rating} field="teamwork_rating" />
      </div>

      <div>
        <label className="text-xs font-medium">Manager Assessment</label>
        <textarea
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={formData.manager_assessment}
          onChange={(e) => setFormData({ ...formData, manager_assessment: e.target.value })}
          placeholder="Overall assessment of employee performance..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium">Strengths</label>
          <textarea
            className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={formData.strengths}
            onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium">Areas for Improvement</label>
          <textarea
            className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={formData.areas_for_improvement}
            onChange={(e) => setFormData({ ...formData, areas_for_improvement: e.target.value })}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave(formData)}>
          <Save size={14} className="mr-1" /> Save Review
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <X size={14} className="mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}
